/**
 * PURPOSE: Co-ordinates post-bootstrap initialization.  This is needed because feature toggles and other data are not available
 *   when the app first loads.
 */

// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { ReduxStore, ReduxNext } from '@makemydeal/dr-shared-store';
import { bridgeUserActionTypes, dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as bridgeUserSelectors from '@makemydeal/dr-dash-store/selectors/bridgeUserSelectors';
import { getCommonOrgId, getDealerDetails, getDealerMMDId, getDealXgId } from '@makemydeal/dr-sales-checkin-store';
import type { BridgeUser } from '@makemydeal/dr-dash-types';
import type { CommonDataLayerV3Props } from '@makemydeal/dr-common-data-layer-utils';
import { initDrCdlV3 } from '@makemydeal/dr-common-data-layer-utils';
import { buildCdlV3ObjectFromState } from '@makemydeal/dr-sales-checkin-ui-utils';
import { publishKnownPendoIdsToNewRelic, publishPendoIdsFromSdkToNewRelic } from '@makemydeal/dr-shared-ui-utils';
import { DEFAULT_PENDO_ACCOUNT_ID } from '../../../constants';
import type { SalesCheckinPendoUser } from '../types/pendo.types';

export const initializePendo = (bridgeUser: BridgeUser, updateDxgIdNode = false) => {
    try {
        window.pendo.initialize({
            visitor: {
                id: bridgeUser.bridgeId,
                full_name: bridgeUser.fullName,
                dc_isRoleAssumption: bridgeUser.isRoleAssumption,
                ...(updateDxgIdNode && { dc_dealXgId: bridgeUser.dealXgId })
            },
            account: {
                id: bridgeUser.accountId ? `${bridgeUser.accountId}` : DEFAULT_PENDO_ACCOUNT_ID,
                dc_dealerName: bridgeUser.dealerName,
                ...(!updateDxgIdNode && { dc_dealXgId: bridgeUser.dealXgId }),
                dc_gtmVariation: bridgeUser.gtmVariation,
                dc_isTestDealer: bridgeUser.isTestDealer
            }
        });

        // Publish Pendo IDs to New Relic so every subsequent PageAction /
        // AjaxRequest / PageView / JavaScriptError inherits them as
        // custom attributes. This enables cross-tool session correlation
        // (Pendo ↔ New Relic) without a bespoke join. US2036660.
        //
        // Mirrors the dash shell's postBootstrapMiddleware; see comments there
        // for the two-call design and the `*FromBridge` attribute rationale.
        const commonOrgId = bridgeUser.accountId ? `${bridgeUser.accountId}` : DEFAULT_PENDO_ACCOUNT_ID;
        publishKnownPendoIdsToNewRelic({ bridgeId: bridgeUser.bridgeId, commonOrgId });
        publishPendoIdsFromSdkToNewRelic({ bridgeId: bridgeUser.bridgeId, commonOrgId });
    } catch (error) {
        console.error('Failed to initialize Pendo:', error);
    }
};

export const initializeCdlV3 = async (store: any) => {
    try {
        const state = store.getState();
        const cdlV3Obj = buildCdlV3ObjectFromState(state);

        initDrCdlV3(cdlV3Obj as CommonDataLayerV3Props);
        store.dispatch({
            type: 'CDLV3_DATA_READY'
        });
    } catch (error) {
        console.error('Could not intialize initDrCdlV3', error);
    }
};

export const postBootstrapMiddleware = (store: ReduxStore<any>) => (next: ReduxNext) => async (action: AnyFSA) => {
    // need reducers to execute first so that we can use the state that's updated by INIT_PENCIL_SUCCESS
    const nextResult = next(action);

    switch (action.type) {
        case bridgeUserActionTypes.BRIDGE_USER_SUCCESS: {
            // here will create the request object for Pendo Dashboard
            const state = store.getState();
            const updateDxgIdNode = featureToggleSelectors.isEnableUpdatePendoMetadata(state);

            const pendoObj: SalesCheckinPendoUser = {
                bridgeId: bridgeUserSelectors.getBridgeId(state),
                fullName: bridgeUserSelectors.getFullName(state),
                accountId: getCommonOrgId(state),
                dealerName: getDealerDetails(state).dealerName,
                dealXgId: getDealXgId(state),
                isTestDealer: dealerSelectors.getIsTestDealer(state),
                isRoleAssumption: state.roleAssumption?.isRoleAssumptionStarted ?? false,
                dealerMMDId: getDealerMMDId(state)?.toString() ?? ''
            };

            initializePendo(pendoObj, updateDxgIdNode);
            initializeCdlV3(store);
            break;
        }
    }

    return nextResult;
};
